/* eslint-disable react/no-unused-prop-types */
import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import styled from 'styled-components';
import { connect, useStore } from 'react-redux';
import { ArrowRight } from 'react-feather';
import loadNamespaces from 'next-translate/loadNamespaces';
import qs from 'query-string';
import { I18nDictionary } from 'next-translate';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { mobile, useIsMobile } from '../../style/constants';
import ImageRightTestimonials from '../../components/Register/ImageRightTestimonials';
import Link from '../../components/common/Link';
import BrowserPageTitle from '../../components/common/BrowserPageTitle';
import { login, withAuthSync } from '../../utils/auth';
import Flex from '../../components/common/Flex';
import Logo from '../../components/common/Logo';
import { Button } from '../../VuexyDesign/atoms/_index';
import {
  Dispatch,
  useCurrentUser,
  User,
  StoreType,
  authenticationActions,
  userActions,
} from '../../api';
import GoogleLoginHandler from '../../components/common/SocialLogin/GoogleLoginHandler';
import virppLogoIcon from '../../public/static/images/Virpp-logo-svg.svg';
import { useAffiliate } from '../../utils/affiliate';
import { registerPusher } from '../../utils/registerPusher';
import { VirpPageContext } from '../_app';

const RegisterPage = ({
  getCurrentUser,
  cb,
}: {
  getCurrentUser: () => Promise<User | undefined>;
  cb: (token: string) => void;
  __lang: string;
  __namespaces?: Record<string, I18nDictionary>;
}) => {
  const { t } = useTranslation('register');
  const [isNewUser, setIsNewUser] = useState(false);
  const { query, push } = useRouter();
  const isMobile = useIsMobile();
  const user = useCurrentUser();
  const affiliate = useAffiliate();
  const store = useStore() as StoreType;
  const router = useRouter();

  const handleEmailRegisterClick = () => {
    if (query.redirect) {
      localStorage.setItem('redirect-url', query.redirect.toString());
    }
  };

  const finalizeLogin = async (
    token: string,
    setSubmitting?: (flag: boolean) => void,
  ) => {
    await login({ token, cb });
    await getCurrentUser();

    if (setSubmitting) {
      setSubmitting(false);
    }
    registerPusher(store);
    affiliate.flushAffilateId();
  };

  /* --------------------------- if user new then it redirects to a Pricing --------------------------- */
  useEffect(() => {
    const { abilities } = user;
    if (abilities) {
      if (abilities?.plan === 'amateur') {
        if (isNewUser) {
          push('/become-pro?newuser=true&fire_ga=true');
        } else {
          push('/become-pro');
        }
      } else {
        push('/profile');
      }
    }
  }, [user]);
  return (
    <Outer>
      <BrowserPageTitle description={t('register:browserPageTitle') as string}>
        {t('register:title') as string}
      </BrowserPageTitle>
      {!isMobile && <ImageRightTestimonials />}
      <LoginContainer>
        <FormContainer>
          <div
            onClick={() => {
              router.push('/');
            }}
            className="w-100 cursor-pointer"
            style={{ paddingBottom: '24px' }}
          >
            <Image
              alt="VIRPP Logo"
              priority
              src={virppLogoIcon}
              height={44.16}
              width={190}
              className="pointer-events-none"
              style={{
                maxWidth: '100%',
              }}
            />
          </div>

          <div className="d-flex flex-column w-100">
            <span className="custom-font-text-larger-medium">
              {t('pricing:yourMusicCareerStartshere') as string} &#128640;
            </span>
            <span className="custom-font-text-small-normal mb-2">
              {t('pricing:nextLevel') as string}
            </span>
          </div>

          <Flex flexDirection="column" width="100%">
            <GoogleLoginHandler
              finalize={({ token, new_user }) => {
                setIsNewUser(new_user);
                finalizeLogin(token);
              }}
              width={!isMobile ? '400px' : '330px'}
              authType="SIGN_UP"
            />
          </Flex>

          <div className="w-100">
            <div className="divider">
              <div className="divider-text text-lowercase text-secondary">
                {t('common:or') as string}
              </div>
            </div>
          </div>
          <div className="w-100">
            <Link href="/register/email/initial">
              <Button
                color="secondary"
                outline
                className="d-flex flex-row align-items-center br-5"
                style={{ gap: '10px' }}
                onClick={handleEmailRegisterClick}
              >
                <span>{t('register:signUpWithEmail') as string}</span>
                <ArrowRight size={16} />
              </Button>
            </Link>
          </div>

          <span className="custom-font-text-regular-normal mt-1 w-100 text-center">
            <span className="text-secondary">
              {t('register:alreadyAccount') as string}
            </span>{' '}
            <Link
              style={{ fontWeight: 400 }}
              className="text-primary"
              href={
                router.query?.redirect
                  ? `/login?${qs.stringify({
                      redirect: router.query.redirect as string,
                    })}`
                  : '/login'
              }
            >
              {t('register:signInInstead') as string}
            </Link>
          </span>
        </FormContainer>
      </LoginContainer>
    </Outer>
  );
};

const Outer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: space-between;
  padding: 32px;
  @media ${mobile} {
    height: auto;
  }
`;

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const FormContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 400px;

  @media ${mobile} {
    width: 330px;
  }

  button {
    margin-bottom: 10px;
  }

  input {
    margin-bottom: 18px;
  }

  input,
  a,
  button {
    width: 100%;
  }

  ${Logo} {
    display: flex;
    width: 100%;
    img {
      margin-left: auto;
      margin-right: auto;
    }
  }

  a.forgot-pass {
    text-align: center;
  }
`;

RegisterPage.getInitialProps = async (ctx: VirpPageContext) => {
  const getTranslations = await loadNamespaces({
    ...ctx,
    pathname: '/register',
  });
  return { ...getTranslations };
};

export default withAuthSync(
  connect(null, (dispatch: Dispatch) => ({
    register: (token: string) =>
      dispatch(authenticationActions.register(token)),
    getCurrentUser: () => dispatch(userActions.getCurrent()),
    cb: (token: string) =>
      dispatch(authenticationActions.verifyLoginOnStartup(token)),
  }))(RegisterPage),
);
